/**
 * CR098: Deal Card Preview Component
 * 
 * Renders a sample deal card for admin preview purposes.
 * 
 * CR100: Now passes full styling config including decoration toggles
 * CR106: Supports global theme token overrides for Theme Labs preview
 * CR111D: Added error boundary, diagnostics overlay, and defensive guards
 * CR305: Added debug render signature strip for pipeline verification
 * CR307: Added Live/Sample data source toggle with unified hook
 * CR313: Admin preview is V2-only — no V1 fallback, no flicker
 */

import { useMemo, useState, useRef, useEffect, useCallback } from "react";
import type { Deal, DealCategory } from "@/lib/types/deals";
import type { DealTypeRegistryEntry } from "@/hooks/useDealCardConfig";
import type { CardStyleConfig } from "@/lib/deals/cardStyleConfig";
import type { GlobalThemeTokens } from "@/lib/deals/globalThemeTokens";
import { DEFAULT_GLOBAL_THEME } from "@/lib/deals/globalThemeTokens";
import { GlobalThemeProvider } from "@/providers/GlobalThemeProvider";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Info, Eye, Star, Maximize2, RefreshCw, AlertCircle } from "lucide-react";
import { 
  PreviewErrorBoundary, 
  PreviewDiagnosticsOverlay,
  computeDraftHash,
  setLastPreviewAction,
} from "@/components/admin/preview";
// Note: FoldLineOverlay now inlined in this component for proper scale handling
import { type FoldLineHeight } from "@/components/deals/FoldLineOverlay";
import { useFoldLineSettings } from "@/hooks/useFoldLineSettings";
import { DealCardRenderSignatureStrip } from "@/components/debug/DealCardRenderSignatureStrip";
import { buildDealCardRenderSignature, type DealCardScreen } from "@/lib/deals/debugDealCardSignature";
import { Skeleton } from "@/components/ui/skeleton";

// CR313: V2 renderer only — no rollout, no V1 fallback
import { DealCardRendererV2 } from "@/components/deal-cards/DealCardRendererV2";
import { useDealCardSpecV2 } from "@/hooks/useDealCardSpecV2";

// CR307: Data source components
import { DataSourceBadge, DataSourceToggle } from "@/components/admin/DataSourceBadge";
import { useDealCardsData, type DealCardsDataSource } from "@/hooks/useDealCardsData";
import { Button } from "@/components/ui/button";

// ================================================================
// SAMPLE DEALS FOR PREVIEW
// ================================================================

const SAMPLE_DEALS: Record<string, Partial<Deal>> = {
  BONUS: {
    merchant: "Shoppers Drug Mart",
    // CR099: Semantic merchant/brand fields
    promo_merchant_name: "Shoppers Drug Mart",
    promo_merchant_key: "shoppers-drug-mart",
    reward_brand_name: "The Keg",
    reward_brand_key: "the-keg",
    reward_type: "gift_card",
    qualifying_product_name: "The Keg gift card",
    validation_flags: [],
    // Legacy fields (for backwards compatibility)
    purchase_gift_card_brand: "The Keg",
    purchase_brand_logo_url: null,
    gift_card_brand: "The Keg",
    bonus_merchant: "The Keg",
    bonus_merchant_logo_url: null,
    bonus_amount: 25,
    spend_amount: 100,
    bonus_or_discount_amount: 25,
    headline_mechanic: "Buy $100 The Keg, get $25 bonus",
    deal_category: "BONUS",
    deal_type: "bonus",
    start_date: new Date().toISOString(),
    end_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
    source_url: "https://example.com",
    source_type: "flyer",
    confidence: "High",
    availability_scope: "all_locations",
    // CR305: Value fields for V2 value boxes
    computed_value_cad: 25,
    effective_percent_back: 25,
    qualifying_spend_cad: 100,
    bonus_value_cad: 25,
    spend_range_min: null,
    spend_range_max: null,
    discount_percent: null,
    discount_tiers: null,
    deal_pricing_type: null,
    fine_print: "",
    channel: "both",
    region: "Canada",
    proof_urls: [],
    confidence_reason: "",
    raw_source_notes: "",
    bonus_valid_from: null,
    bonus_valid_until: null,
    requires_physical_card: null,
    claim_url: null,
    redeem_url: null,
    claim_deadline: null,
    campaign_name: null,
  },
  
  DISCOUNT: {
    merchant: "Walmart",
    purchase_gift_card_brand: "Walmart",
    purchase_brand_logo_url: null,
    gift_card_brand: "Walmart",
    bonus_merchant: null,
    bonus_merchant_logo_url: null,
    bonus_amount: null,
    spend_amount: null,
    bonus_or_discount_amount: null,
    discount_percent: 10,
    headline_mechanic: "10% cash back on Walmart gift cards",
    deal_category: "DISCOUNT",
    deal_type: "discount",
    start_date: new Date().toISOString(),
    end_date: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(),
    source_url: "https://example.com",
    source_type: "website",
    confidence: "High",
    availability_scope: "all_locations",
    computed_value_cad: 10,
    effective_percent_back: 10,
    spend_range_min: null,
    spend_range_max: null,
    discount_tiers: null,
    deal_pricing_type: null,
    fine_print: "",
    channel: "both",
    region: "Canada",
    proof_urls: [],
    confidence_reason: "",
    raw_source_notes: "",
    bonus_valid_from: null,
    bonus_valid_until: null,
    requires_physical_card: null,
    claim_url: null,
    redeem_url: null,
    claim_deadline: null,
    campaign_name: null,
  },
  
  POINTS: {
    merchant: "PC Optimum",
    purchase_gift_card_brand: "Loblaws",
    purchase_brand_logo_url: null,
    gift_card_brand: "Loblaws",
    bonus_merchant: null,
    bonus_merchant_logo_url: null,
    bonus_amount: null,
    spend_amount: null,
    bonus_or_discount_amount: null,
    points_amount: 5000,
    points_program_name: "PC Optimum",
    headline_mechanic: "Earn 5,000 PC Optimum points on $50 Loblaws gift card",
    deal_category: "POINTS",
    deal_type: "points",
    start_date: new Date().toISOString(),
    end_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
    source_url: "https://example.com",
    source_type: "flyer",
    confidence: "Medium",
    availability_scope: "participating_locations",
    availability_notes: "Ontario only",
    computed_value_cad: 5,
    effective_percent_back: 10,
    spend_range_min: null,
    spend_range_max: null,
    discount_percent: null,
    discount_tiers: null,
    deal_pricing_type: null,
    fine_print: "",
    channel: "in-store",
    region: "Ontario",
    proof_urls: [],
    confidence_reason: "",
    raw_source_notes: "",
    bonus_valid_from: null,
    bonus_valid_until: null,
    requires_physical_card: null,
    claim_url: null,
    redeem_url: null,
    claim_deadline: null,
    campaign_name: null,
  },
  
  IN_KIND: {
    merchant: "Tim Hortons",
    purchase_gift_card_brand: "Tim Hortons",
    purchase_brand_logo_url: null,
    gift_card_brand: "Tim Hortons",
    bonus_merchant: null,
    bonus_merchant_logo_url: null,
    bonus_amount: null,
    spend_amount: null,
    bonus_or_discount_amount: null,
    headline_mechanic: "Buy $25, get a free coffee",
    deal_category: "IN_KIND",
    deal_type: "in_kind",
    in_kind_tiers: [{ spend_cad: 25, reward_qty: 1, reward_item_type: "PRODUCT", reward_item_name: "Free coffee" }],
    start_date: new Date().toISOString(),
    end_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
    source_url: "https://example.com",
    source_type: "website",
    confidence: "High",
    availability_scope: "all_locations",
    spend_range_min: null,
    spend_range_max: null,
    discount_percent: null,
    discount_tiers: null,
    deal_pricing_type: null,
    fine_print: "",
    channel: "both",
    region: "Canada",
    proof_urls: [],
    confidence_reason: "",
    raw_source_notes: "",
    bonus_valid_from: null,
    bonus_valid_until: null,
    requires_physical_card: null,
    claim_url: null,
    redeem_url: null,
    claim_deadline: null,
    campaign_name: null,
  },
  
  UNCLASSIFIED: {
    merchant: "Unknown Store",
    purchase_gift_card_brand: "Unknown",
    purchase_brand_logo_url: null,
    gift_card_brand: "Unknown",
    bonus_merchant: null,
    bonus_merchant_logo_url: null,
    bonus_amount: null,
    spend_amount: null,
    bonus_or_discount_amount: null,
    headline_mechanic: "Special offer - details TBD",
    deal_category: "UNCLASSIFIED",
    deal_type: "unclassified",
    source_url: "https://example.com",
    source_type: "other",
    confidence: "Low",
    availability_scope: "unknown",
    spend_range_min: null,
    spend_range_max: null,
    discount_percent: null,
    discount_tiers: null,
    deal_pricing_type: null,
    fine_print: "",
    channel: "unknown",
    region: "Canada",
    proof_urls: [],
    confidence_reason: "",
    raw_source_notes: "",
    bonus_valid_from: null,
    bonus_valid_until: null,
    requires_physical_card: null,
    claim_url: null,
    redeem_url: null,
    claim_deadline: null,
    campaign_name: null,
  },
};

// ================================================================
// COMPONENT
// ================================================================

// Extended template type with styling - use CardStyleConfig for decoration toggles
interface ExtendedCardTemplateJsonPreview {
  sections?: Record<string, boolean>;
  labels?: Record<string, string>;
  header?: Record<string, unknown>;
  extended_sections?: Record<string, boolean>;
  styling?: Partial<CardStyleConfig>;
  section_order?: string[];
  theme_preset?: "light" | "dark" | "custom";
}

interface DealCardPreviewProps {
  dealType: string;
  registry?: DealTypeRegistryEntry;
  template?: ExtendedCardTemplateJsonPreview | null;
  /** CR106: Optional global theme token override for Theme Labs preview */
  globalThemeOverride?: GlobalThemeTokens | null;
  /** CR111D: Optional callback when preview is reset after crash */
  onPreviewReset?: () => void;
  /** CR111D: Context identifier for diagnostics (e.g., "ThemeLabs", "DealCards") */
  previewContext?: string;
  /** CR111D: Last action for diagnostics */
  lastAction?: string;
  /** CR305: Screen identifier for debug signature */
  debugScreen?: DealCardScreen;
  /** CR307: Enable Live/Sample data source toggle */
  showDataSourceToggle?: boolean;
}

export function DealCardPreview({ 
  dealType, 
  registry, 
  template, 
  globalThemeOverride,
  onPreviewReset,
  previewContext = "DealCardPreview",
  lastAction,
  debugScreen = "deal_cards_ui",
  showDataSourceToggle = false,
}: DealCardPreviewProps) {
  // CR313: Admin preview is V2-only — always load spec (no rollout, no V1 fallback)
  const { spec: v2Spec, loading: v2SpecLoading } = useDealCardSpecV2({ dealTypeKey: dealType });
  
  // CR307: Live/Sample data source hook
  const {
    deals: liveDeals,
    meta: dataSourceMeta,
    source: dataSource,
    setSource: setDataSource,
    loading: dataSourceLoading,
    refresh: refreshDataSource,
  } = useDealCardsData({
    dealTypeFilter: dealType,
    limit: 10,
    persistSource: true,
  });
  
  // CR154: Use new fold line settings with per-size heights
  const { settings: foldSettings, getHeightForSize } = useFoldLineSettings();
  const foldEnabled = foldSettings.enabled;
  // Default to "normal" (regular) card size for single preview
  const foldHeightPx = getHeightForSize("normal");

  // CR305: Auto-height mode to show all content without cropping
  const [autoHeightMode, setAutoHeightMode] = useState(false);

  // CR111D: Track render count for diagnostics
  const renderCountRef = useRef(0);
  renderCountRef.current += 1;
  
  // CR111D: Track render time
  const renderStartRef = useRef(performance.now());
  const [renderTimeMs, setRenderTimeMs] = useState(0);
  
  useEffect(() => {
    setRenderTimeMs(performance.now() - renderStartRef.current);
  });

  // CR111D: Compute draft hash for diagnostics
  const draftHash = useMemo(() => {
    return computeDraftHash({ globalThemeOverride, template, registry });
  }, [globalThemeOverride, template, registry]);

  // CR111D/CR208: Log action when props change significantly
  useEffect(() => {
    if (globalThemeOverride || template) {
      console.log("[CR208-DealCardPreview] Props updated", {
        dealType,
        hasGlobalTheme: !!globalThemeOverride,
        draftHash,
        renderCount: renderCountRef.current,
      });
      setLastPreviewAction(`Preview updated for ${dealType}`);
    }
  }, [dealType, globalThemeOverride, template, draftHash]);

  // CR307: Determine which deal to render
  // If data source toggle is enabled and we have live deals, use the first matching one
  const effectiveDeal = useMemo<Deal>(() => {
    // If using live data and we have deals available
    if (showDataSourceToggle && dataSource === "live" && liveDeals.length > 0) {
      // Find first deal that matches the dealType
      const liveDeal = liveDeals.find(d => {
        const dt = (d.deal_type || d.deal_category || "").toString().toUpperCase();
        return dt === dealType.toUpperCase();
      }) || liveDeals[0];
      
      if (liveDeal) {
        return liveDeal as unknown as Deal;
      }
    }
    
    // Fallback to static sample deal
    try {
      const base = SAMPLE_DEALS[dealType] || SAMPLE_DEALS.UNCLASSIFIED;
      return {
        ...base,
        deal_category: dealType as DealCategory,
        deal_type: dealType.toLowerCase() as Deal["deal_type"],
      } as Deal;
    } catch (err) {
      console.error("[DealCardPreview] Error creating sample deal:", err);
      return SAMPLE_DEALS.UNCLASSIFIED as Deal;
    }
  }, [dealType, showDataSourceToggle, dataSource, liveDeals]);
  
  // Legacy alias for backwards compatibility
  const sampleDeal = effectiveDeal;

  // CR313: Preview ready when V2 spec loaded
  const previewReady = !!v2Spec && !v2SpecLoading;

  // CR111D: Safely merge global theme override with defaults
  const safeGlobalThemeOverride = useMemo(() => {
    if (!globalThemeOverride) return null;
    try {
      // Deep merge with defaults to ensure no undefined fields
      return {
        ...DEFAULT_GLOBAL_THEME,
        ...globalThemeOverride,
        typography: { 
          ...DEFAULT_GLOBAL_THEME.typography, 
          ...(globalThemeOverride.typography || {}) 
        },
        colors: { 
          ...DEFAULT_GLOBAL_THEME.colors, 
          ...(globalThemeOverride.colors || {}) 
        },
        spacing: { 
          ...DEFAULT_GLOBAL_THEME.spacing, 
          ...(globalThemeOverride.spacing || {}) 
        },
        components: { 
          ...DEFAULT_GLOBAL_THEME.components, 
          ...(globalThemeOverride.components || {}),
          ribbon: {
            ...DEFAULT_GLOBAL_THEME.components.ribbon,
            ...(globalThemeOverride.components?.ribbon || {}),
          },
          badge: {
            ...DEFAULT_GLOBAL_THEME.components.badge,
            ...(globalThemeOverride.components?.badge || {}),
          },
          button: {
            ...DEFAULT_GLOBAL_THEME.components.button,
            ...(globalThemeOverride.components?.button || {}),
          },
          starburst: {
            ...DEFAULT_GLOBAL_THEME.components.starburst,
            ...(globalThemeOverride.components?.starburst || {}),
          },
        },
      } as GlobalThemeTokens;
    } catch (err) {
      console.error("[DealCardPreview] Error merging global theme override:", err);
      return null;
    }
  }, [globalThemeOverride]);

  // Extract styling info for chips display - use CardStyleConfig shape
  const styling = template?.styling as Partial<CardStyleConfig> | undefined;
  const themePreset = template?.theme_preset;

  // CR111D: Reset handler for error boundary
  const handlePreviewReset = useCallback(() => {
    setLastPreviewAction("Preview reset after crash");
    onPreviewReset?.();
  }, [onPreviewReset]);

  return (
    <div className="space-y-4">
      {/* CR307: Data Source Toggle (if enabled) */}
      {showDataSourceToggle && (
        <div className="flex items-center justify-between gap-4 p-3 rounded-lg border bg-muted/30">
          <div className="flex items-center gap-3">
            <DataSourceToggle
              source={dataSource}
              onSourceChange={setDataSource}
              disabled={dataSourceLoading}
            />
            <DataSourceBadge
              source={dataSource}
              publishState={dataSourceMeta.publishState}
              dealCount={liveDeals.length}
              compact
            />
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => refreshDataSource()}
            disabled={dataSourceLoading}
            className="h-8 px-2"
          >
            <RefreshCw className={`h-4 w-4 ${dataSourceLoading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      )}
      
      {/* Preview Info */}
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Info className="h-4 w-4" />
        <span>
          {showDataSourceToggle && dataSource === "live" && liveDeals.length > 0
            ? `Showing LIVE ${registry?.label || dealType} deal`
            : `Showing sample ${registry?.label || dealType} deal`}
        </span>
        {/* CR313: Admin preview is always V2 */}
        <Badge variant="default" className="text-xs">V2</Badge>
        
        {/* CR305: Auto-height toggle */}
        <div className="flex items-center gap-1.5 ml-auto">
          <Maximize2 className="h-3 w-3 text-muted-foreground" />
          <span className="text-xs">Auto Height</span>
          <Switch
            checked={autoHeightMode}
            onCheckedChange={setAutoHeightMode}
            className="h-4 w-7 data-[state=checked]:bg-primary"
          />
        </div>
        
        <Badge variant="outline" className="text-xs">
          Live from draft
        </Badge>
      </div>
      
      {/* Registry Display (Ribbon settings) */}
      {registry && (
        <div className="flex flex-wrap gap-2 text-xs">
          <Badge variant="outline">
            Label: {registry.label}
          </Badge>
          <Badge variant="outline">
            Ribbon: {registry.ribbon_text}
          </Badge>
          <Badge variant="outline" className="flex items-center gap-1">
            <div className={`w-2 h-2 rounded-full ${
              {
                blue: "bg-blue-600",
                orange: "bg-orange-500",
                purple: "bg-purple-600",
                green: "bg-green-600",
                gray: "bg-gray-500",
                navy: "bg-slate-800",
                red: "bg-red-600",
                amber: "bg-amber-600",
                emerald: "bg-emerald-600",
                teal: "bg-teal-600",
              }[registry.ribbon_theme] || "bg-gray-500"
            }`} />
            Theme: {registry.ribbon_theme}
          </Badge>
          <Badge variant={registry.is_enabled ? "default" : "secondary"}>
            {registry.is_enabled ? "Enabled" : "Disabled"}
          </Badge>
        </div>
      )}

      {/* Template Styling Display (from draft) - includes decoration toggles */}
      {styling && (
        <div className="flex flex-wrap gap-2 text-xs border-t pt-2">
          {themePreset && (
            <Badge variant="outline" className="flex items-center gap-1">
              <div className={`w-2 h-2 rounded-full ${
                themePreset === "dark" ? "bg-slate-800" : 
                themePreset === "light" ? "bg-white border border-gray-300" : 
                "bg-gradient-to-r from-purple-500 to-pink-500"
              }`} />
              Preset: {themePreset}
            </Badge>
          )}
          {styling.cornerRadius !== undefined && (
            <Badge variant="outline">
              Radius: {styling.cornerRadius}px
            </Badge>
          )}
          {styling.shadowIntensity && (
            <Badge variant="outline">
              Shadow: {styling.shadowIntensity}
            </Badge>
          )}
          {styling.backgroundType && (
            <Badge variant="outline">
              BG: {styling.backgroundType}
            </Badge>
          )}
          {/* Header element toggle indicators */}
          <Badge 
            variant={styling.showMerchantLogo !== false ? "default" : "secondary"}
            className="flex items-center gap-1"
          >
            <Eye className="h-3 w-3" />
            Logo: {styling.showMerchantLogo !== false ? "ON" : "OFF"}
          </Badge>
          <Badge 
            variant={styling.showMerchantName !== false ? "default" : "secondary"}
            className="flex items-center gap-1"
          >
            <Eye className="h-3 w-3" />
            Name: {styling.showMerchantName !== false ? "ON" : "OFF"}
          </Badge>
        </div>
      )}

      {/* Template Sections Active */}
      {template && template.sections && (
        <div className="text-xs text-muted-foreground">
          Active sections:{" "}
          {Object.entries(template.sections)
            .filter(([, v]) => v)
            .map(([k]) => k.replace(/_/g, " "))
            .join(", ") || "None"}
        </div>
      )}

      {/* Actual Card Preview - NOW WITH OVERRIDES */}
      {/* CR106: Wrap in GlobalThemeProvider if override tokens provided */}
      {/* CR111D: Wrap in error boundary to prevent blanking admin */}
      {/* CR154: Card wrapper with fold line overlay using configurable heights */}
      {/* CR305: Auto-height mode removes constraints for layout design */}
      <div 
        className="border rounded-lg p-4 bg-muted/20 overflow-visible" 
        style={{ 
          minHeight: autoHeightMode ? 'auto' : (foldEnabled ? '720px' : 'auto'), 
          paddingBottom: autoHeightMode ? '16px' : (foldEnabled ? '100px' : '16px'),
        }}
      >
        {/* CR305: Auto-height mode indicator */}
        {autoHeightMode && (
          <div className="mb-2 text-xs text-muted-foreground bg-muted/50 px-2 py-1 rounded flex items-center gap-1.5">
            <Maximize2 className="h-3 w-3" />
            Auto Height ON — all sections expanded, no cropping
          </div>
        )}
        
        {/* CR111D: Diagnostics overlay (dev-only) */}
        <PreviewDiagnosticsOverlay
          mode={safeGlobalThemeOverride || template ? "live" : "static"}
          dealType={dealType}
          draftHash={draftHash}
          lastAction={lastAction}
          renderCount={renderCountRef.current}
          renderTimeMs={renderTimeMs}
        />
        
        <PreviewErrorBoundary
          previewContext={previewContext}
          onReset={handlePreviewReset}
        >
          {/* Card container - position relative for fold line overlay */}
          {/* Scale is 90%, so fold line position is adjusted accordingly */}
          {/* CR305: Auto-height mode removes height constraints */}
          <div 
            className="relative overflow-visible" 
            style={{ minHeight: autoHeightMode ? 'auto' : (foldEnabled ? '680px' : 'auto') }}
          >
            <div className={autoHeightMode ? "origin-top-left" : "scale-90 origin-top-left"}>
              {/* CR313: V2-only Admin Preview — never mount V1 */}
              {v2SpecLoading && (
                <Skeleton className="w-[340px] h-[440px] rounded-lg" />
              )}

              {!v2SpecLoading && !v2Spec && (
                <div className="w-[340px] h-[440px] rounded-lg border-2 border-dashed border-destructive/50 bg-destructive/5 flex flex-col items-center justify-center p-4 text-center">
                  <AlertCircle className="h-8 w-8 text-destructive mb-2" />
                  <p className="text-sm font-medium text-destructive">V2 Preview failed to load</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    No V2 spec available for {dealType}. Check deal_card_templates / overrides_json + resolver.
                  </p>
                </div>
              )}

              {previewReady && (
                safeGlobalThemeOverride ? (
                  <GlobalThemeProvider overrideTokens={safeGlobalThemeOverride}>
                    <DealCardRendererV2
                      deal={sampleDeal}
                      spec={v2Spec}
                      isAdmin={true}
                      surface="primary"
                    />
                  </GlobalThemeProvider>
                ) : (
                  <DealCardRendererV2
                    deal={sampleDeal}
                    spec={v2Spec}
                    isAdmin={true}
                    surface="primary"
                  />
                )
              )}
            </div>
            {/* CR154: Overlay - position at scaled coordinates (card is at 90%) */}
            {/* CR305: Hide fold line in auto-height mode */}
            {foldEnabled && !autoHeightMode && (
              <div 
                className="absolute left-0 right-0 pointer-events-none z-30"
                style={{ top: `${foldHeightPx * 0.9}px` }}
              >
                {/* Dead-simple dotted line */}
                <div className="w-full border-t-2 border-dashed border-muted-foreground" />
                {/* Label */}
                <div className="absolute right-2 -top-4">
                  <span className="inline-flex items-center px-2 py-0.5 rounded bg-background border border-muted-foreground/50 text-xs font-medium text-muted-foreground shadow-sm">
                    Fold: {foldHeightPx}px
                  </span>
                </div>
              </div>
            )}
          </div>
        </PreviewErrorBoundary>
      </div>

      {/* CR313: Debug Render Signature Strip - shows context info */}
      <DealCardRenderSignatureStrip
        sig={buildDealCardRenderSignature({
          screen: debugScreen,
          deal: {
            id: (effectiveDeal as any).id || null,
            dedupe_key: (effectiveDeal as any).dedupe_key || null,
            merchant_key: sampleDeal.merchant?.toLowerCase().replace(/\s+/g, "-") ?? null,
            deal_type: dealType,
            // CR307: Reflect actual data source in signature
            source: showDataSourceToggle && dataSource === "live" && liveDeals.length > 0 
              ? "cr075_production_deals" 
              : "mock",
          },
          layout: {
            environment: "draft",
            active_preset_id: null,
            preset_updated_at: null,
          },
          theme: {
            tokens_source: globalThemeOverride ? "theme_labs_preview" : "global_theme_tokens",
            theme_id: null,
          },
          config: {
            resolver: "layout_studio_resolver",
            uses_deal_card_display_helpers: true,
          },
        })}
      />

      {/* Updated note */}
      <p className="text-xs text-muted-foreground italic">
        {showDataSourceToggle && dataSource === "live"
          ? "Showing live production data. Toggle to Sample for static test data."
          : "Preview updates instantly from your draft changes. Click Save to persist."}
      </p>
    </div>
  );
}
